import {createBrowserHistory} from 'history'
import size from 'lodash/size'
import {Reducer, applyMiddleware, compose, createStore} from 'redux'
import {createReduxHistoryContext} from 'redux-first-history'
import {install as installReduxLoop} from 'redux-loop'

import {Actions} from './actions'
import createReducer, {State} from './reducers'

import {createSentryReduxEnhancer} from '../../commons/src/services/logging'

import {Order} from './order-types'

export default (initialState = {}) => {
  const history = createBrowserHistory()

  const {createReduxHistory, routerMiddleware, routerReducer} = createReduxHistoryContext({
    history,
  })

  let enhancer = compose(
    installReduxLoop(),
    applyMiddleware(routerMiddleware),
    createSentryReduxEnhancer({
      stateTransformer: ({core, order}: State) => ({
        // core
        ...core,
        colors: size(core.colors),
        countries: size(core.countries),
        finishGroups: size(core.finishGroups),
        materialConfigs: size(core.materialConfigs),
        materials: size(core.materials),
        materialGroups: size(core.materialGroups),
        printingMethods: size(core.printingMethods),
        provider: core.provider?.id,
        // order
        orders: size(order.orders),
        orderStatusFilterValues: order.statusFilter.serialize().filterValues,
      }),
      actionTransformer: (action: Actions) => {
        if (action.type === 'CORE.INITIAL_DATA_RECEIVED') {
          return {
            type: action.type,
            payload: {
              // Only show lengths of newly fetched initial core data to minimize Sentry breadcrumb size
              colors: size(action.payload.colors),
              countries: size(action.payload.countries),
              finishGroups: size(action.payload.finishGroups),
              materialConfigs: size(action.payload.materialConfigs),
              materials: size(action.payload.materials),
              materialGroups: size(action.payload.materialGroups),
              printingMethods: size(action.payload.printingMethods),
              provider: action.payload.provider?.id,
            },
          }
        }
        if (action.type === 'ORDER.ORDERS_RECEIVED') {
          return {
            type: action.type,
            // Only track orderNumber from received orders to minimize Sentry breadcrumb size
            payload: action.payload.map((order: Order) => order.orderNumber),
          }
        }
        return action
      },
    })
  )

  if (global.__REDUX_DEVTOOLS_EXTENSION__) {
    // Enable redux dev-tools
    enhancer = compose(enhancer, global.__REDUX_DEVTOOLS_EXTENSION__())
  }

  const store = createStore(createReducer(routerReducer) as Reducer, initialState, enhancer)

  if (module.hot) {
    // Enable Webpack hot module replacement for reducers
    module.hot.accept('./reducers', () => {
      // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
      const nextReducer = require('./reducers').default
      store.replaceReducer(nextReducer)
    })
  }

  return {
    store,
    history: createReduxHistory(store),
  }
}
